import { CommonModule } from '@angular/common';
import {
    Component,
    computed,
    effect,
    inject,
    Injector,
    Input,
    input,
    InputSignal,
    OnInit,
    Signal,
    signal,
    untracked,
    WritableSignal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { DatiqTemplateModule } from '@dat/datiq-components-pkg/template';
import {
    IqEventBusBrokerHandler,
    IqEventBusBrokerService,
} from '@dat/iq-event-bus';
import { IqBuilderLabelComponent } from '@dat/iq-style-processor';
import { isUndefined } from 'lodash';
import { Observable } from 'rxjs'
import { emptyTab, Tab } from '../models';

@Component({
    selector: 'iq-control-tab-tabs',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        DatiqTemplateModule,
        MatIconModule,
        IqBuilderLabelComponent,
    ],
    templateUrl: './iq-control-tab-tabs.component.html',
    styleUrl: './iq-control-tab-tabs.component.scss',
})
export class IqControlTabTabsComponent implements OnInit {
    id: InputSignal<string> = input('');
    testId: InputSignal<string> = input('');
    currentTestId: Signal<string> = computed(() => {
        return this.testId()?.length ? this.testId() : this.id();
    });
    name: InputSignal<string> = input('');
    tabs: InputSignal<Tab[]> = input([]);
    value: InputSignal<Tab> = input({});
    disabled: InputSignal<boolean> = input(false);

    updateTabs: WritableSignal<Tab[]> = signal([]);

    @Input()
    fg: FormGroup = new FormGroup({});

    inputControl = null;
    inputValue: Signal<Tab> = signal({});
    input$: Observable<Tab>;

    currentTab: Signal<Tab> = computed(() => {
        const tab: Tab =
            this.tabs().find((t) => t?.value === this.value()?.value) ?? {};

        return tab;
    });

    private _events: IqEventBusBrokerService = inject(IqEventBusBrokerService);
    private _dispatchEvents: Record<string, IqEventBusBrokerHandler>;

    constructor(
        private readonly _fb: FormBuilder,
        private readonly injector: Injector,
    ) {
        effect(() => {
            if (isUndefined(this.value()) || this.disabled()) return null;
            const tab: Tab = this.tabs().find(
                (t) => t.value === this.value().value,
            );

            if (tab && tab.disabled) return null;

            untracked(() => {
                this.inputControl.setValue(tab);
                /**
                 * This dispatches an event indicating the tabs have changed without the user interaction
                 *
                 *
                 * To listen to these events, you can use the following format in your Dashboard, Section, or Component:
                 *
                 * ```json
                 * {
                 *  "onChange": [
                 *    {
                 *      "name": "EVENT_NAME",
                 *      "payload": {},
                 *      "sourceEventRule": {
                 *          "payload": [
                 *            {
                 *                "path": "control.id",
                 *                "operator": "eq",
                 *                "value": "CONTROL_ID"
                 *            },
                 *          ]
                 *      },
                 *      "rules": []
                 *    },
                 *   ]
                 * }
                 * ```
                 */
                const dispatch = () =>
                    this._events.dispatch({
                        name: 'type:change',
                        payload: {
                            id: this.id(),
                            componentName: 'IqControlTabTabs',
                            ...this._createEmitData(),
                            tab,
                            value: tab,
                        },
                    });

                dispatch();
            });
        });
    }

    ngOnInit(): void {
        this.fg.addControl(this.name(), this._fb.control({}));
        this.inputControl = this.fg?.get(this.name()) as FormControl;

        this.inputControl.setValue(this.currentTab());

        this.inputValue = toSignal(this.inputControl.valueChanges, {
            injector: this.injector,
        });
    }

    tabClick(tab: Tab): void {
        if (tab.disabled) return;

        this.inputControl.setValue(tab);
        /**
         * This dispatches an event indicating the tabs have changed with the user interaction
         *
         * To listen to these events, you can use the following format in your Dashboard, Section, or Component:
         *
         * ```json
         * {
         *  "onClick": [
         *    {
         *      "name": "EVENT_NAME",
         *      "payload": {},
         *      "sourceEventRule": {
         *          "payload": [
         *            {
         *                "path": "control.id",
         *                "operator": "eq",
         *                "value": "CONTROL_ID"
         *            },
         *            {
         *                "path": "control.name",
         *                "operator": "eq",
         *                "value": "NAME"
         *            },
         *          ]
         *      },
         *      "rules": []
         *    },
         *   ]
         * }
         * ```
         */
        const dispatch = () =>
            this._events.dispatch({
                name: 'type:click',
                payload: {
                    id: this.id(),
                    componentName: 'IqControlTabTabs',
                    ...this._createEmitData(),
                    tab,
                    value: tab,
                },
            });

        dispatch();
    }

    handleTooltipClick(tab: Tab) {
        this._events.dispatch({
            name: 'type:tooltip:click',
            payload: {
                id: this.id(),
                componentName: 'IqControlTabTabs',
                ...this._createEmitData(),
                tab,
                value: tab,
            },
        });
    }

    private _createEmitData(): { [key: string]: any } {
        const emitData = {
            form: {
                ...this.fg.value,
                valid: this.fg.valid,
                errors: this.fg.errors,
            },
            control: {
                id: this.id(),
                tabs: this.tabs(),
                value: this.value(),
                name: this.name(),
            },
        };
        return emitData;
    }
}
